import type { TNode } from '../terrain/TerrainQuadtree';

interface Item {
  node: TNode;
  dist: number;
}

const byDist = (a: Item, b: Item) => a.dist - b.dist;

/**
 * The nodes nearest the camera, at most `max` of them (a vegetation buffer's size). Taking
 * the first `max` in draw order instead dropped nodes next to the player whenever more were
 * in range, and which ones depended on the way the camera faced. In small worlds that was
 * most of the time. Reuses its items, so it allocates nothing once warmed up.
 */
export class NearestNodes {
  private readonly items: Item[] = [];
  count = 0;
  /** Distance of the nearest node left out (Infinity when none were). */
  cut = Number.POSITIVE_INFINITY;

  constructor(readonly max: number) {}

  reset(): void {
    this.count = 0;
    this.cut = Number.POSITIVE_INFINITY;
  }

  add(node: TNode, dist: number): void {
    const item = this.items[this.count];
    if (item) {
      item.node = node;
      item.dist = dist;
    } else {
      this.items.push({ node, dist });
    }
    this.count++;
  }

  /** Keep the nearest `max` (if there are more) and return how many are kept. */
  finish(): number {
    if (this.count > this.max) {
      // Stale items from busier frames sort last.
      for (let i = this.count; i < this.items.length; i++) this.items[i].dist = Number.POSITIVE_INFINITY;
      this.items.sort(byDist);
      this.cut = this.items[this.max].dist;
      this.count = this.max;
    }
    return this.count;
  }

  node(i: number): TNode {
    return this.items[i].node;
  }
}
